/**
 * Non-combat skills, and the points that buy them.
 *
 * Editable, like the inventory and unlike the rest of the sheet — how a
 * character spent their study is genuinely the player's. **What it is worth is
 * not**: the tier modifiers, the budget and what each skill costs are all the
 * server's, re-derived on every write and served with the catalogue, so nothing
 * here does arithmetic on a rule.
 *
 * The remaining figure comes back from the server rather than being counted
 * locally for that same reason — the free starting skill makes the sum not
 * quite the sum, and a client that guessed it would disagree with the refusal
 * it eventually got.
 */

import { titleLabel } from '../labels.js';
import { useState } from 'react';

export interface SkillDescriptor {
  readonly name: string;
  readonly family: string;
  readonly classGroups: readonly string[];
  readonly ability?: string;
  readonly difficulty?: number;
  readonly cost?: number;
  readonly untrainedAllowed?: boolean;
  readonly summary?: string;
}

export interface HeldSkill {
  readonly name: string;
  readonly tier: string;
}

export interface SkillsSectionProps {
  readonly held: readonly HeldSkill[];
  readonly catalogue: readonly SkillDescriptor[];
  /** The free Trade or Knowledge skill, whose proficiency costs nothing. */
  readonly bonusSkill?: string;
  readonly spent: number;
  readonly available?: number;
  /**
   * The class groups this character satisfies a restriction with, from the
   * server's `derived`.
   *
   * **Not derived here.** A class name maps to a group through
   * `classes.json` → `classGroups`, which is a rule — and the rule of this file
   * is that nothing in it does arithmetic on one. Absent means the server could
   * not say, and then **nothing is filtered**: showing a skill that will be
   * refused is a smaller fault than hiding one the character can actually take.
   */
  readonly classGroups?: readonly string[];
  /** Writes the whole list; the server re-derives the total from it. */
  readonly onChange: (skills: readonly HeldSkill[]) => void;
  readonly readOnly?: boolean;
  /** A refusal of the last change, shown here beside the control, not only at the top of the page. */
  readonly notice?: string | null;
}

/**
 * Whether this character may learn this skill.
 *
 * **Offered at all, rather than offered and refused.** The picker used to list
 * the whole printed table with the restriction rendered as a parenthetical —
 * `forgery — general, 1 pt (rogue)` on a Mage's sheet — and selecting one was
 * possible, so the UI displayed a rule and then declined to apply it. A skill
 * you cannot take is not worth seeing — ineligible skills are not offered at
 * all, rather than offered and refused.
 *
 * ⚠ **Class GROUPS, and any one of them will do.** The column is written in
 * groups (`"Rogue/Warrior"`) and a multi-classed character "retains the best …
 * features among its classes" — so a fighter/thief satisfies a rogue skill and
 * a warrior one. Matching the primary class alone would deny a multi-classed
 * character half of what it is entitled to.
 *
 * **An unrestricted skill is open to everyone**: an empty `classGroups` on the
 * entry is the book printing no restriction, not a restriction nobody meets.
 *
 * Exported and pure so the rule can be tested without rendering: the bug and
 * the fix produce almost identical markup, which is the shape that has slipped
 * through here before.
 */
export function canTake(
  entry: Pick<SkillDescriptor, 'classGroups'>,
  classGroups: readonly string[] | undefined,
): boolean {
  if (entry.classGroups.length === 0) return true;
  // Unknown groups filter nothing — see the prop's note.
  if (classGroups === undefined) return true;
  return entry.classGroups.some((group) => classGroups.includes(group));
}

/**
 * Why a skill cannot be learned right now, or `undefined` when it can. Maz, 2026-10-21: with every point spent the
 * Learn button was still live and the refusal landed at the top of the sheet, out of sight. The server's figure
 * for what is left is the only one used (`available - spent`); a skill with no printed cost is never blocked on
 * cost here, because the server still decides.
 */
export function learnBlock(
  remaining: number | undefined,
  entry: Pick<SkillDescriptor, 'cost'> | undefined,
): string | undefined {
  if (remaining === undefined) return undefined;
  if (remaining <= 0) return 'All skill points are spent';
  if (entry?.cost !== undefined && entry.cost > remaining) {
    return `Costs ${entry.cost} ${entry.cost === 1 ? 'point' : 'points'}; ${remaining} left`;
  }
  return undefined;
}

/** What the sheet says about the points: how many are spent and how many are still there to spend. */
export function pointsLine(spent: number, available: number | undefined): string {
  if (available === undefined) return `${spent} spent`;
  const left = Math.max(0, available - spent);
  return `${spent} spent · ${left === 0 ? 'none left to spend' : `${left} left to spend`}`;
}

/** The tiers a character can hold. `untrained` is the absence of them. */
const TIERS = ['proficient', 'advanced', 'mastery'] as const;

export function SkillsSection({
  held,
  catalogue,
  bonusSkill,
  spent,
  available,
  classGroups,
  onChange,
  readOnly,
  notice,
}: SkillsSectionProps) {
  const [picked, setPicked] = useState('');

  const describe = (name: string): SkillDescriptor | undefined =>
    catalogue.find((entry) => entry.name === name);

  const remaining = available === undefined ? undefined : available - spent;
  const blocked = learnBlock(remaining, picked === '' ? undefined : describe(picked));

  const add = (): void => {
    if (!picked || blocked || held.some((s) => s.name === picked)) return;
    onChange([...held, { name: picked, tier: 'proficient' }]);
    setPicked('');
  };

  return (
    <section className="space-y-2">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Skills</h2>
      {/* Roll-under, and it says so — the saving throws two sections up are the
          opposite comparison, and a sheet that showed both without saying which
          is which teaches the wrong one. */}
      <p className="text-xs text-neutral-500 dark:text-neutral-400">
        roll d20 <strong>under</strong> the ability score — natural 1 always succeeds, natural 20
        always fails
      </p>

      <ul className="space-y-1 text-sm">
        {held.length === 0 && <li className="text-neutral-500 dark:text-neutral-400">none</li>}
        {held.map((skill) => {
          const entry = describe(skill.name);
          return (
            <li key={skill.name} className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{titleLabel(skill.name)}</span>
              <span className="text-xs text-neutral-500 dark:text-neutral-400">
                {entry?.ability ?? 'no check'}
                {entry?.difficulty ? ` ${entry.difficulty > 0 ? '+' : ''}${entry.difficulty}` : ''}
                {skill.name === bonusSkill ? ' · free at creation' : ''}
              </span>
              <select
                aria-label={`${titleLabel(skill.name)} proficiency`}
                value={skill.tier}
                disabled={readOnly}
                onChange={(e) =>
                  onChange(
                    held.map((s) => (s.name === skill.name ? { ...s, tier: e.target.value } : s)),
                  )
                }
                className="rounded-md border border-neutral-300 bg-white px-1 py-0.5 text-xs dark:border-neutral-700 dark:bg-neutral-900"
              >
                {TIERS.map((tier) => (
                  <option key={tier} value={tier}>
                    {tier}
                  </option>
                ))}
              </select>
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => onChange(held.filter((s) => s.name !== skill.name))}
                  className="rounded-md border border-neutral-300 px-2 py-0.5 text-xs dark:border-neutral-700"
                >
                  drop
                </button>
              )}
            </li>
          );
        })}
      </ul>

      <p className="text-xs text-neutral-500 dark:text-neutral-400 tabular-nums">
        {pointsLine(spent, available)}
      </p>

      {!readOnly && catalogue.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <select
            aria-label="A skill to learn"
            value={picked}
            onChange={(e) => setPicked(e.target.value)}
            className="rounded-md border border-neutral-300 bg-white px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-900"
          >
            <option value="">choose a skill…</option>
            {catalogue
              .filter((entry) => !held.some((s) => s.name === entry.name))
              .filter((entry) => canTake(entry, classGroups))
              .map((entry) => (
                <option key={entry.name} value={entry.name} disabled={learnBlock(remaining, entry) !== undefined} title={learnBlock(remaining, entry)}>
                  {titleLabel(entry.name)} — {entry.family}
                  {entry.cost === undefined ? '' : `, ${entry.cost} pt`}
                  {/* Still labelled, for the ones that ARE offered: a
                      multi-classed character sees which of its groups is
                      paying for the skill. */}
                  {entry.classGroups.length > 0 ? ` (${entry.classGroups.join('/')})` : ''}
                </option>
              ))}
          </select>
          {/* Greyed, with the reason on hover, when nothing can be learned: all points spent, or this skill costs
              more than is left. A span carries the title because a disabled button shows none in some browsers. */}
          <span title={blocked ?? (picked === '' ? 'Choose a skill first' : undefined)}>
            <button
              type="button"
              onClick={add}
              disabled={picked === '' || blocked !== undefined}
              aria-describedby={blocked ? 'skills-learn-reason' : undefined}
              className="rounded-md border border-neutral-300 px-3 py-1 text-sm disabled:cursor-not-allowed disabled:opacity-40 dark:border-neutral-700"
            >
              Learn
            </button>
          </span>
          {blocked && (
            <span id="skills-learn-reason" className="text-xs text-neutral-500 dark:text-neutral-400">
              {blocked}
            </span>
          )}
        </div>
      )}

      {notice && (
        <p role="alert" className="text-xs text-amber-800 dark:text-amber-300">
          {notice}
        </p>
      )}

      {picked !== '' && describe(picked)?.summary && (
        <p className="text-xs text-neutral-500 dark:text-neutral-400">{describe(picked)?.summary}</p>
      )}
    </section>
  );
}
